import { WizardConfig, WizardConfigArgs } from './types'
import { NeedsAssessmentWizardStep } from './constants'
import { InviteYourOrgLeader } from './components/InviteYourOrgLeader/InviteYourOrgLeader'
import { GetStarted } from './components/GetStarted/GetStarted'
import { WhatIsYourRole } from './components/WhatIsYourRole/WhatIsYourRole'
import { WhatIsYourPrimaryFunction } from './components/WhatIsYourPrimaryFunction/WhatIsYourPrimaryFunction'
import { Priorities } from './components/Priorities/Priorities'
import { WeHaveCapturedYourPriorities } from './components/WeHaveCapturedYourPriorities/WeHaveCapturedYourPriorities'
import { ThankYouForSharing } from './components/ThankYouForSharing/ThankYouForSharing'
import { WhatAreWeMissing } from './components/WhatAreWeMissing/WhatAreWeMissing'
import { WeSuggestTheseGoals } from './components/WeSuggestTheseGoals/WeSuggestTheseGoals'
import { AdditionalPriorities } from './components/AdditionalPriorities/AdditionalPriorities'
import { BaselineConfidence } from './components/BaselineConfidence/BaselineConfidence'
import { BaselineSupportNeeded } from './components/BaselineSupportNeeded/BaselineSupportNeeded'
import { AddYourMissionStatement } from './components/AddYourMissionStatement/AddYourMissionStatement'

const {
  GET_STARTED,
  WHAT_IS_YOUR_ROLE,
  WHAT_IS_YOUR_PRIMARY_FUNCTION,
  INVITE_YOUR_ORG_LEADER,
  SELECT_YOUR_PRIORITIES,
  WE_HAVE_CAPTURED_YOUR_PRIORITIES,
  ADDITIONAL_PRIORITIES,
  THANK_YOU_FOR_SHARING,
  WHAT_ARE_WE_MISSING,
  WE_SUGGEST_THESE_GOALS,
  ADD_YOUR_MISSION_STATEMENT,
  BASELINE_CONFIDENCE,
  BASELINE_SUPPORT_NEEDED,
} = NeedsAssessmentWizardStep

/*
  This config is used to create the various steps for the Needs Assessment wizard.
  Each step is passed a custom component to render, and any additional configuration needed.
  All the handlers and values that each step needs are passed from the parent state.
 */
export const createWizardConfig = ({
  baselineConfidence,
  baselineSupportNeeded,
  nonprofitOrgId,
  onAnyAction,
  onBookmarkResources,
  onToggleTemplateGoal,
  onFetchRecommendedResources,
  onInviteOrgLeader,
  onSetBaselineConfidence,
  onSetBaselineSupportNeeded,
  onSetOrgLeaderInvite,
  onSetPriorities,
  onSetRecommendedResources,
  onSetUserPrimaryFunction,
  onSetUserRole,
  onSelectTemplateGoals,
  orgLeaderInvite,
  priorities,
  recommendedResources,
  resourcesToBookmark,
  templateGoalsToCreate,
  templateLibraryMap,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WizardConfigArgs): WizardConfig => ({
  [GET_STARTED]: {
    component: (
      <GetStarted
        onAnyAction={onAnyAction}
        userPrimaryFunction={userPrimaryFunction}
        userJourneyLength={userJourneyLength}
      />
    ),
    hideBackButton: true,
  },
  [WHAT_IS_YOUR_ROLE]: {
    component: (
      <WhatIsYourRole
        onAnyAction={onAnyAction}
        onSetUserRole={onSetUserRole}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [WHAT_IS_YOUR_PRIMARY_FUNCTION]: {
    component: (
      <WhatIsYourPrimaryFunction
        onAnyAction={onAnyAction}
        onSetUserPrimaryFunction={onSetUserPrimaryFunction}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [BASELINE_CONFIDENCE]: {
    component: (
      <BaselineConfidence
        baselineConfidence={baselineConfidence}
        onAnyAction={onAnyAction}
        onSetBaselineConfidence={onSetBaselineConfidence}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [BASELINE_SUPPORT_NEEDED]: {
    component: (
      <BaselineSupportNeeded
        baselineSupportNeeded={baselineSupportNeeded}
        onAnyAction={onAnyAction}
        onSetBaselineSupportNeeded={onSetBaselineSupportNeeded}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [INVITE_YOUR_ORG_LEADER]: {
    component: (
      <InviteYourOrgLeader
        onAnyAction={onAnyAction}
        onInviteOrgLeader={onInviteOrgLeader}
        orgLeaderInvite={orgLeaderInvite}
        setOrgLeaderInvite={onSetOrgLeaderInvite}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
      />
    ),
  },
  [SELECT_YOUR_PRIORITIES]: {
    component: (
      <Priorities
        onAnyAction={onAnyAction}
        onToggleTemplateGoal={onToggleTemplateGoal}
        onSetPriorities={onSetPriorities}
        onSetRecommendedResources={onSetRecommendedResources}
        priorities={priorities}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [WE_HAVE_CAPTURED_YOUR_PRIORITIES]: {
    component: (
      <WeHaveCapturedYourPriorities
        onAnyAction={onAnyAction}
        onSetPriorities={onSetPriorities}
        priorities={priorities}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [ADDITIONAL_PRIORITIES]: {
    component: (
      <AdditionalPriorities
        onAnyAction={onAnyAction}
        onToggleTemplateGoal={onToggleTemplateGoal}
        onSetPriorities={onSetPriorities}
        onSetRecommendedResources={onSetRecommendedResources}
        priorities={priorities}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [THANK_YOU_FOR_SHARING]: {
    component: (
      <ThankYouForSharing
        onAnyAction={onAnyAction}
        onBookmarkResources={onBookmarkResources}
        onFetchRecommendedResources={onFetchRecommendedResources}
        priorities={priorities}
        recommendedResources={recommendedResources}
        resourcesToBookmark={resourcesToBookmark}
      />
    ),
    showCloseIcon: true,
  },
  [WHAT_ARE_WE_MISSING]: {
    component: (
      <WhatAreWeMissing
        onAnyAction={onAnyAction}
        onSetPriorities={onSetPriorities}
        onSetRecommendedResources={onSetRecommendedResources}
        priorities={priorities}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [WE_SUGGEST_THESE_GOALS]: {
    component: (
      <WeSuggestTheseGoals
        onAnyAction={onAnyAction}
        onSelectTemplateGoals={onSelectTemplateGoals}
        priorities={priorities}
        templateGoalsToCreate={templateGoalsToCreate}
        templateLibraryMap={templateLibraryMap}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
  [ADD_YOUR_MISSION_STATEMENT]: {
    component: (
      <AddYourMissionStatement
        nonprofitOrgId={nonprofitOrgId}
        onAnyAction={onAnyAction}
        userJourneyLength={userJourneyLength}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
      />
    ),
  },
})
